import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { apiError } from "@/lib/api";
import { Logo } from "@/components/Navbar";
import { toast } from "sonner";

function googleLogin() {
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

const MARKETPLACES = ["Amazon.com", "Amazon.in", "Amazon.co.uk", "Amazon.de", "Amazon.ca", "Amazon.com.au", "Other"];
const REV_BANDS = ["< $1K/mo", "$1K – $10K/mo", "$10K – $50K/mo", "$50K – $250K/mo", "$250K+/mo"];

function Label({ children, htmlFor }) {
  return <label htmlFor={htmlFor} className="label-mono mb-1.5 block">{children}</label>;
}

export default function Signup() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    email: "", password: "", name: "", business_name: "",
    marketplace: "", monthly_revenue_band: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await register(form);
      toast.success("Account created. Welcome.");
      const pending = sessionStorage.getItem("pending_asin");
      if (pending) {
        sessionStorage.removeItem("pending_asin");
        navigate(`/tools/catalog-analyzer?q=${encodeURIComponent(pending)}`);
      } else { navigate("/dashboard"); }
    } catch (err) { setError(apiError(err, "Could not create account")); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-paper">
      <main className="flex items-center justify-center p-8 lg:p-16 order-2 lg:order-1">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8"><Logo /></div>
          <div className="label-mono">Get started</div>
          <h1 className="mt-3 font-display font-black text-4xl md:text-5xl tracking-tightest text-ink leading-[1]">
            Create your free account.
          </h1>
          <p className="text-ink/60 mt-3">Five free runs per analyst, every day. No card.</p>

          <Button variant="outline" type="button" className="w-full mt-8 h-11"
            onClick={googleLogin} data-testid="google-signup-btn">
            <GoogleIcon /> Continue with Google
          </Button>
          <div className="flex items-center gap-3 my-6 label-mono">
            <div className="h-px bg-rule flex-1" />OR<div className="h-px bg-rule flex-1" />
          </div>

          <form onSubmit={submit} className="space-y-4" data-testid="signup-form">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="su-name">Name</Label>
                <Input id="su-name" data-testid="signup-name" value={form.name}
                  onChange={(e) => set("name", e.target.value)} required />
              </div>
              <div>
                <Label htmlFor="su-email">Email</Label>
                <Input id="su-email" data-testid="signup-email" type="email" value={form.email}
                  onChange={(e) => set("email", e.target.value)} required />
              </div>
            </div>
            <div>
              <Label htmlFor="su-pass">Password · min 8 chars</Label>
              <Input id="su-pass" data-testid="signup-password" type="password" minLength={8}
                value={form.password} onChange={(e) => set("password", e.target.value)} required />
            </div>
            <div>
              <Label htmlFor="su-biz">Business · optional</Label>
              <Input id="su-biz" data-testid="signup-biz" value={form.business_name}
                onChange={(e) => set("business_name", e.target.value)} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Marketplace</Label>
                <Select value={form.marketplace} onValueChange={(v) => set("marketplace", v)}>
                  <SelectTrigger data-testid="signup-marketplace" className="rounded-none border-rule h-10"><SelectValue placeholder="Optional" /></SelectTrigger>
                  <SelectContent className="rounded-none border-rule">{MARKETPLACES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Monthly revenue</Label>
                <Select value={form.monthly_revenue_band} onValueChange={(v) => set("monthly_revenue_band", v)}>
                  <SelectTrigger data-testid="signup-revenue" className="rounded-none border-rule h-10"><SelectValue placeholder="Optional" /></SelectTrigger>
                  <SelectContent className="rounded-none border-rule">{REV_BANDS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            {error && (
              <div className="text-sm text-alert border border-alert/30 bg-alert-bg px-3 py-2" data-testid="signup-error">
                {error}
              </div>
            )}
            <Button type="submit" disabled={loading} className="w-full h-11" data-testid="signup-submit">
              {loading ? "Creating…" : "Create my account"}
            </Button>
          </form>
          <p className="text-sm text-ink/60 mt-6">
            Already have an account?{" "}
            <Link to="/login" className="font-medium text-ink underline underline-offset-4 hover:text-ink-2" data-testid="signup-login-link">
              Log in →
            </Link>
          </p>
        </div>
      </main>

      <aside className="hidden lg:flex flex-col justify-between p-12 bg-cobalt text-white relative overflow-hidden order-1 lg:order-2">
        <div className="absolute inset-0 grid-bg opacity-[0.12]" aria-hidden />
        <div className="absolute top-16 right-12 w-28 h-28 bg-butter rotate-6" aria-hidden />
        <div className="absolute bottom-32 right-16 w-16 h-16 bg-persimmon -rotate-12" aria-hidden />
        <div className="relative"><Logo /></div>
        <div className="relative max-w-md">
          <div className="label-mono text-white/70">№ 01 · The deal</div>
          <h2 className="mt-6 font-display font-bold text-4xl leading-[1.05] tracking-tightest">
            Catalog Analyzer. Listing Optimizer. Keyword Generator. <span className="bg-butter text-ink px-2">All free.</span> 5 runs / tool / day.
          </h2>
          <div className="mt-6 text-white/80 leading-relaxed">
            Built so any seller — not just the $100K/mo ones — can compete.
          </div>
        </div>
        <div className="relative label-mono text-white/60">© {new Date().getFullYear()} · CliqCommerce</div>
      </aside>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-4 h-4 mr-2"><path fill="#111" d="M12 11v3.2h7.3c-.3 1.7-2 4.9-7.3 4.9-4.4 0-8-3.6-8-8s3.6-8 8-8c2.5 0 4.2 1 5.2 2l3.5-3.3C18.5 1 15.5 0 12 0 5.4 0 0 5.4 0 12s5.4 12 12 12c6.9 0 11.5-4.9 11.5-11.7 0-.8-.1-1.4-.2-2H12z"/></svg>
  );
}
